import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getAdminPw, setAdminPw, isAdmin, clearAdminPw, getActiveClubId } from "../lib/api";
import { Lock, Plus, Trash2, Download, Upload, Play, Users, LayoutGrid, RotateCcw, Image as ImageIcon, ArrowUp, ArrowDown, Eye, EyeOff, Shuffle, Trash, Mail, Archive, Pencil } from "lucide-react";
export default function Admin() {
  const [authed, setAuthed] = useState(isAdmin());
  const [pw, setPw] = useState("");

  const login = async () => {
    try { await api.adminLogin(pw); setAdminPw(pw); setAuthed(true); toast.success("Angemeldet"); }
    catch { toast.error("Falsches Passwort"); }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto dc-border p-8 mt-12" data-testid="admin-login">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={20} style={{ color: "var(--dc-primary)" }} />
          <h1 className="font-display text-3xl">Admin-Login</h1>
        </div>
        <p className="text-sm mb-4" style={{ color: "var(--dc-muted)" }}>Nur der Admin kann Spieler, Saisons, Spieltage und Backups verwalten.</p>
        <input type="password" className="dc-input mb-3" placeholder="Passwort" value={pw}
          onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && login()}
          data-testid="admin-password-input" />
        <button className="dc-btn w-full justify-center" onClick={login} data-testid="admin-login-button">
          <Lock size={14} /> Anmelden
        </button>
      </div>
    );
  }

  return <AdminPanel />;
}

function AdminPanel() {
  const [tab, setTab] = useState("players");
  return (
    <div className="space-y-6" data-testid="admin-panel">
      <div>
        <div className="text-xs uppercase tracking-[0.4em]" style={{ color: "var(--dc-chalk)" }}>Verwaltung</div>
        <h1 className="font-display text-5xl">Admin</h1>
      </div>
      <div className="flex flex-wrap gap-2 border-b dc-border overflow-x-auto">
        {[
          ["players", "Spieler"], ["seasons", "Saisons"], ["matchdays", "Spieltage"],
          ["historic", "Historisch"], ["dashboard", "Dashboard"], ["backup", "Backup"],
        ].map(([k, l]) => (
          <button key={k} className={`dc-tab whitespace-nowrap ${tab === k ? "active" : ""}`} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}>{l}</button>
        ))}
      </div>
      {tab === "players" && <PlayersManager />}
      {tab === "seasons" && <SeasonsManager />}
      {tab === "matchdays" && <MatchdaysManager />}
      {tab === "historic" && <HistoricManager />}
      {tab === "dashboard" && <DashboardManager />}
      {tab === "backup" && <BackupManager />}
    </div>
  );
}

function PlayersManager() {
  const [players, setPlayers] = useState([]);
  const [name, setName] = useState(""); const [nick, setNick] = useState("");
  const reload = async () => setPlayers(await api.listPlayers());
  useEffect(() => { reload(); }, []);

  const add = async () => {
    if (!name.trim()) return;
    try { await api.createPlayer({ name: name.trim(), nickname: nick.trim() || null }); setName(""); setNick(""); toast.success("Spieler hinzugefügt"); reload(); }
    catch { toast.error("Fehler"); }
  };
  const del = async (id) => {
    if (!window.confirm("Spieler wirklich löschen?")) return;
    await api.deletePlayer(id); toast.success("Gelöscht"); reload();
  };
  const rename = async (player) => {
    const name = window.prompt("Spielername ändern", player.name);
    if (name === null || !name.trim() || name.trim() === player.name) return;
    try { await api.patchPlayer(player.id, { name: name.trim() }); toast.success("Name geändert – alle Ergebnisse bleiben erhalten"); reload(); }
    catch (error) { toast.error(error?.response?.data?.detail || "Änderung fehlgeschlagen"); }
  };

  return (
    <div className="space-y-4" data-testid="players-manager">
      <div className="dc-border p-6 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Name</label>
          <input className="dc-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="player-name-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Nickname (optional)</label>
          <input className="dc-input" value={nick} onChange={(e) => setNick(e.target.value)} data-testid="player-nickname-input" />
        </div>
        <button className="dc-btn" onClick={add} data-testid="player-add-button"><Plus size={14} /> Hinzufügen</button>
      </div>
      <div className="dc-border">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase" style={{ color: "var(--dc-muted)" }}><th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Nickname</th><th className="w-20"></th></tr></thead>
          <tbody>
            {players.map((p) => (
              <tr key={p.id} className="border-t dc-border" data-testid={`player-row-${p.id}`}>
                <td className="px-4 py-2">{p.name}</td>
                <td className="px-4 py-2" style={{ color: "var(--dc-muted)" }}>{p.nickname || "—"}</td>
                <td className="text-right px-4 whitespace-nowrap"><button className="p-2 hover:text-[color:var(--dc-primary)]" title="Name ändern" onClick={() => rename(p)} data-testid={`player-rename-${p.id}`}><Pencil size={14} /></button><button className="p-2 hover:text-[color:var(--dc-primary)]" onClick={() => del(p.id)} data-testid={`player-delete-${p.id}`}><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {players.length === 0 && <tr><td colSpan={3} className="text-center py-6" style={{ color: "var(--dc-muted)" }}>Noch keine Spieler.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SeasonsManager() {
  const [seasons, setSeasons] = useState([]);
  const [players, setPlayers] = useState([]);
  const [name, setName] = useState("");
  const [days, setDays] = useState(0);
  const [selected, setSelected] = useState([]);
  const reload = async () => {
    const [ss, ps] = await Promise.all([api.listSeasons(), api.listPlayers()]);
    setSeasons(ss); setPlayers(ps);
  };
  useEffect(() => { reload(); }, []);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const create = async () => {
    if (!name.trim() || selected.length < 2) { toast.error("Name und ≥ 2 Stammspieler nötig"); return; }
    try {
      const res = await api.createSeason({ name: name.trim(), member_ids: selected, num_matchdays: days });
      toast.success(`Saison "${name}" erstellt${res.matchdays_created ? ` · ${res.matchdays_created} Start-Spieltage` : ""}`);
      setName(""); setSelected([]); reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Saison konnte nicht erstellt werden");
      reload();
    }
  };
  const activate = async (id) => { await api.activateSeason(id); toast.success("Aktiviert"); reload(); };
  const archive = async (season) => {
    if (!window.confirm(`Saison "${season.name}" archivieren? Sie bleibt im Adminbereich weiterhin bearbeitbar.`)) return;
    try { await api.archiveSeason(season.id); toast.success("Saison archiviert"); reload(); }
    catch (error) { toast.error(error?.response?.data?.detail || "Saison konnte nicht archiviert werden"); }
  };
  const restore = async (season) => {
    try { await api.restoreSeason(season.id); toast.success("Saison wiederhergestellt"); reload(); }
    catch (error) { toast.error(error?.response?.data?.detail || "Saison konnte nicht wiederhergestellt werden"); }
  };
  const emailProfiles = async (season) => {
    try {
      const result = await api.emailSeasonProfiles(season.id);
      toast.success(`${result.players} Spielerprofile wurden an ${result.recipient} gesendet`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Spielerprofile konnten nicht verschickt werden");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Saison und alle Spieltage/Partien löschen?")) return;
    await api.deleteSeason(id); toast.success("Gelöscht"); reload();
  };

  return (
    <div className="space-y-4" data-testid="seasons-manager">
      <div className="dc-border p-6 space-y-4">
        <h3 className="font-display text-2xl">Neue Saison</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Name</label>
            <input className="dc-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="season-name-input" placeholder="z.B. Saison 2026" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Start-Spieltage</label>
            <input type="number" min="0" max="52" className="dc-input" value={days} onChange={(e) => setDays(Math.max(0, parseInt(e.target.value) || 0))} data-testid="season-days-input" />
            <p className="text-xs mt-1" style={{ color: "var(--dc-muted)" }}>Optional. Weitere Spieltage kannst du jederzeit hinzufügen.</p>
          </div>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest mb-2" style={{ color: "var(--dc-muted)" }}>Stammspieler ({selected.length} ausgewählt)</label>
          <p className="text-xs mb-2" style={{ color: "var(--dc-muted)" }}>Stammspieler gehören fest zur Saison. Gäste werden pro Spieltag ergänzt und zählen ab ihrem ersten Spiel ebenfalls in den Tabellen.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {players.map((p) => (
              <label key={p.id} className={`dc-border px-3 py-2 cursor-pointer flex items-center gap-2 transition-colors ${selected.includes(p.id) ? "bg-[color:var(--dc-secondary)]/10" : "hover:bg-[color:var(--dc-surface-hover)]"}`}
                style={{ borderColor: selected.includes(p.id) ? "var(--dc-secondary)" : "var(--dc-border)" }}
                data-testid={`season-player-${p.id}`}>
                <input type="checkbox" className="accent-[color:var(--dc-secondary)]" checked={selected.includes(p.id)} onChange={() => toggle(p.id)} />
                <span>{p.name}</span>
              </label>
            ))}
          </div>
          {players.length === 0 && <p className="text-sm" style={{ color: "var(--dc-muted)" }}>Bitte zuerst Spieler anlegen.</p>}
        </div>
        <button className="dc-btn dc-btn-secondary" onClick={create} data-testid="season-create-button"><Plus size={14} /> Saison erstellen</button>
      </div>

      <div className="dc-border">
        <table className="w-full text-sm">
          <thead><tr className="text-xs uppercase" style={{ color: "var(--dc-muted)" }}>
            <th className="text-left px-4 py-2">Name</th><th className="text-left px-4 py-2">Spieler</th>
            <th className="text-left px-4 py-2">Tage</th><th className="text-left px-4 py-2">Status</th><th className="w-64"></th>
          </tr></thead>
          <tbody>
            {seasons.map((s) => (
              <tr key={s.id} className="border-t dc-border" data-testid={`season-row-${s.id}`}>
                <td className="px-4 py-2">{s.name}</td>
                <td className="px-4 py-2 font-mono-data">{(s.member_ids || s.player_ids || []).length}</td>
                <td className="px-4 py-2 font-mono-data">{s.num_matchdays ?? 0}</td>
                <td className="px-4 py-2">{s.archived ? <span className="px-2 py-1 text-[10px] uppercase tracking-widest" style={{ background: "var(--dc-muted)", color: "white" }}>Archiviert</span> : s.active ? <span className="px-2 py-1 text-[10px] uppercase tracking-widest" style={{ background: "var(--dc-secondary)", color: "white" }}>Aktiv</span> : <span style={{ color: "var(--dc-muted)" }}>Inaktiv</span>}</td>
                <td className="text-right px-4 space-x-2">
                  {!s.active && <button className="dc-btn dc-btn-ghost text-xs" onClick={() => activate(s.id)} data-testid={`season-activate-${s.id}`}><Play size={12} /> Aktivieren</button>}
                  <button className="dc-btn dc-btn-ghost text-xs" onClick={() => emailProfiles(s)} data-testid={`season-email-profiles-${s.id}`}><Mail size={12} /> Profile mailen</button>
                  {s.archived ? <button className="dc-btn dc-btn-ghost text-xs" onClick={() => restore(s)} data-testid={`season-restore-${s.id}`}><RotateCcw size={12} /> Wiederherstellen</button> : <button className="dc-btn dc-btn-ghost text-xs" onClick={() => archive(s)} data-testid={`season-archive-${s.id}`}><Archive size={12} /> Archivieren</button>}
                  <button className="p-2 hover:text-[color:var(--dc-primary)]" onClick={() => del(s.id)} data-testid={`season-delete-${s.id}`}><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
            {seasons.length === 0 && <tr><td colSpan={5} className="text-center py-6" style={{ color: "var(--dc-muted)" }}>Noch keine Saison.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MatchdaysManager() {
  const [seasons, setSeasons] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [matchdays, setMatchdays] = useState([]);
  const [players, setPlayers] = useState([]);

  const reloadDays = async (sid) => setMatchdays(await api.listMatchdays(sid));
  useEffect(() => {
    (async () => {
      const [ss, ps] = await Promise.all([api.listSeasons(), api.listPlayers()]);
      setSeasons(ss); setPlayers(ps);
      const active = ss.find((s) => s.active) || ss[0];
      if (active) { setSeasonId(active.id); reloadDays(active.id); }
    })();
  }, []);

  useEffect(() => { if (seasonId) reloadDays(seasonId); }, [seasonId]);

  const season = seasons.find((s) => s.id === seasonId);
  const memberIds = new Set(season?.member_ids || []);
  const addDay = async () => {
    if (!seasonId) return;
    try { const day = await api.addMatchday(seasonId); toast.success(`Spieltag ${day.number} hinzugefügt`); reloadDays(seasonId); }
    catch (error) { toast.error(error?.response?.data?.detail || "Spieltag konnte nicht hinzugefügt werden"); }
  };

  return (
    <div className="space-y-4" data-testid="matchdays-manager">
      <div className="dc-border p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Saison</label>
          <select className="dc-input" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} data-testid="matchdays-season-select">
            {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button className="dc-btn dc-btn-secondary" onClick={addDay} data-testid="matchday-add-button"><Plus size={14} /> Spieltag hinzufügen</button>
      </div>
      <div className="space-y-3">
        {matchdays.map((md) => (
          <MatchdayCard key={md.id} md={md} players={players} memberIds={memberIds} onReload={() => reloadDays(seasonId)} />
        ))}
        {matchdays.length === 0 && <div className="dc-border p-6 text-center" style={{ color: "var(--dc-muted)" }}>Keine Spieltage.</div>}
      </div>
    </div>
  );
}

function MatchdayCard({ md, players, memberIds, onReload }) {
  const [expanded, setExpanded] = useState(false);
  const [attendees, setAttendees] = useState(md.attendee_ids || []);
  const [date, setDate] = useState(md.date || "");
  const [includeReturnLeg, setIncludeReturnLeg] = useState(Boolean(md.include_return_leg));
  useEffect(() => { setAttendees(md.attendee_ids || []); setDate(md.date || ""); setIncludeReturnLeg(Boolean(md.include_return_leg)); }, [md]);

  const toggle = (id) => setAttendees((a) => a.includes(id) ? a.filter((x) => x !== id) : [...a, id]);
  const setAllMembers = () => setAttendees(Array.from(memberIds));

  const save = async () => {
    try { await api.patchMatchday(md.id, { attendee_ids: attendees, date: date || null, include_return_leg: includeReturnLeg }); toast.success("Gespeichert"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const generate = async () => {
    try {
      const r = await api.generateMatchday(md.id, { include_return_leg: includeReturnLeg });
      toast.success(`Spielplan erstellt: ${r.matches_created} Partien`);
      onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };
  const clear = async () => {
    if (!window.confirm("Alle unerledigten Partien dieses Spieltags löschen?")) return;
    try { await api.clearMatchday(md.id); toast.success("Zurückgesetzt"); onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  return (
    <div className="dc-border p-4" data-testid={`admin-matchday-${md.number}`}>
      <div className="flex justify-between items-center cursor-pointer" onClick={() => setExpanded((v) => !v)}>
        <div>
          <div className="font-display text-xl">Spieltag {md.number}</div>
          <div className="text-xs" style={{ color: "var(--dc-muted)" }}>
            {(md.attendee_ids || []).length} Anwesende · {md.date || "kein Datum"} · {md.generated ? "Spielplan erstellt" : "offen"}
          </div>
        </div>
        <button className="dc-btn dc-btn-ghost text-xs" data-testid={`admin-matchday-toggle-${md.number}`}>{expanded ? "Zuklappen" : "Bearbeiten"}</button>
      </div>

      {expanded && (
        <div className="space-y-4 pt-4 border-t dc-border mt-4">
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Datum (optional)</label>
            <input type="date" className="dc-input max-w-xs" value={date} onChange={(e) => setDate(e.target.value)} data-testid={`admin-md-date-${md.number}`} />
          </div>
          <div>
            <div className="flex justify-between mb-2">
              <label className="text-xs uppercase tracking-widest" style={{ color: "var(--dc-muted)" }}>
                Anwesenheit ({attendees.length} ausgewählt)
              </label>
              <button className="dc-btn dc-btn-ghost text-xs" onClick={setAllMembers} data-testid={`admin-md-all-members-${md.number}`}>
                <Users size={12} /> Alle Stammspieler
              </button>
            </div>
            <p className="text-xs mb-2" style={{ color: "var(--dc-muted)" }}>Nicht-Stammspieler werden automatisch als Gäste markiert (gelb).</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {players.map((p) => {
                const isGuest = !memberIds.has(p.id);
                const on = attendees.includes(p.id);
                return (
                  <label key={p.id} className="dc-border px-3 py-2 cursor-pointer flex items-center gap-2 hover:bg-[color:var(--dc-surface-hover)]"
                    style={{ borderColor: on ? (isGuest ? "var(--dc-chalk)" : "var(--dc-secondary)") : "var(--dc-border)",
                             background: on ? (isGuest ? "rgba(245,209,59,0.12)" : "rgba(35,148,88,0.10)") : "transparent" }}
                    data-testid={`admin-md-${md.number}-p-${p.id}`}>
                    <input type="checkbox" className="accent-[color:var(--dc-secondary)]" checked={on} onChange={() => toggle(p.id)} />
                    <span>{p.name}</span>
                    {isGuest && <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--dc-chalk)" }}>Gast</span>}
                  </label>
                );
              })}
            </div>
          </div>
          <label className="dc-border px-3 py-3 cursor-pointer flex items-start gap-3 hover:bg-[color:var(--dc-surface-hover)]">
            <input type="checkbox" className="mt-1 accent-[color:var(--dc-secondary)]" checked={includeReturnLeg}
              onChange={(e) => setIncludeReturnLeg(e.target.checked)} data-testid={`admin-md-return-leg-${md.number}`} />
            <span>
              <span className="block font-semibold">Rückrunde zusätzlich erzeugen</span>
              <span className="block text-xs mt-1" style={{ color: "var(--dc-muted)" }}>
                Jede Paarung wird ein zweites Mal mit vertauschter Spielerreihenfolge angesetzt.
              </span>
            </span>
          </label>
          <div className="flex flex-wrap gap-2">
            <button className="dc-btn" onClick={save} data-testid={`admin-md-save-${md.number}`}>Speichern</button>
            <button className="dc-btn dc-btn-secondary" onClick={generate} disabled={attendees.length < 2} data-testid={`admin-md-generate-${md.number}`}>
              <Shuffle size={14} /> Spielplan generieren
            </button>
            {md.generated && (
              <button className="dc-btn dc-btn-ghost" onClick={clear} data-testid={`admin-md-clear-${md.number}`}>
                <RotateCcw size={14} /> Spielplan zurücksetzen
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DashboardManager() {
  const [seasons, setSeasons] = useState([]);
  const [club, setClub] = useState(null);
  const [seasonId, setSeasonId] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [title, setTitle] = useState("");
  const [widgets, setWidgets] = useState([]);
  const reload = async () => {
    const [ss, clubs] = await Promise.all([api.listSeasons(), api.listClubs()]);
    setSeasons(ss);
    setClub(clubs.find((item) => item.id === getActiveClubId()) || null);
    const active = ss.find((s) => s.active) || ss[0];
    if (active) applySeason(active);
    else {
      setSeasonId("");
      setLogoUrl((clubs.find((item) => item.id === getActiveClubId()) || {}).logo_url || "");
      setTitle("");
      setWidgets([]);
    }
  };
  const applySeason = (s) => {
    setSeasonId(s.id);
    setLogoUrl(s.custom_logo_url || "");
    setTitle(s.dashboard_config?.title || "");
    const defaultWidgets = [
      { id: "hero", visible: true }, { id: "leaders", visible: true }, { id: "streaks", visible: true },
      { id: "record_week", visible: true }, { id: "h2h_highlight", visible: true },
      { id: "standings", visible: true }, { id: "upcoming", visible: true },
    ];
    const stored = s.dashboard_config?.widgets || [];
    const storedIds = new Set(stored.map((w) => w.id));
    const merged = [...stored, ...defaultWidgets.filter((w) => !storedIds.has(w.id))];
    setWidgets(merged);
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => {
    if (!seasonId) return;
    const s = seasons.find((x) => x.id === seasonId);
    if (s) applySeason(s);
  }, [seasonId]);

  const move = (idx, dir) => {
    const nx = idx + dir; if (nx < 0 || nx >= widgets.length) return;
    const cp = [...widgets]; [cp[idx], cp[nx]] = [cp[nx], cp[idx]]; setWidgets(cp);
  };
  const toggleVis = (idx) => setWidgets((w) => w.map((x, i) => i === idx ? { ...x, visible: !x.visible } : x));

  const save = async () => {
    try {
      if (!club) { toast.error("Gruppe nicht gefunden"); return; }
      await api.patchClub(club.id, { logo_url: logoUrl || null });
      if (seasonId) await api.patchSeason(seasonId, { dashboard_config: { widgets, title: title || null } });
      toast.success("Dashboard aktualisiert");
      reload();
    } catch (e) { toast.error("Fehler"); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Maximal 5 MB pro Logo"); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const widgetLabels = {
    hero: "Hero-Kachel (Aktuelle Saison)",
    leaders: "Leader-Kacheln (Top Avg / 180er / HF)",
    streaks: "Siegesserien (längste / aktuell)",
    record_week: "Rekord der Woche (letzte 7 Tage)",
    h2h_highlight: "Head-to-Head Highlight",
    standings: "Tabellen-Vorschau",
    upcoming: "Nächste Partien",
  };

  return (
    <div className="space-y-4" data-testid="dashboard-manager">
      <div className="dc-border p-4">
        <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Saison</label>
        <select className="dc-input" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} data-testid="dashboard-season-select">
          {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
      </div>

      {!seasonId && (
        <div className="dc-border p-4 text-sm" style={{ color: "var(--dc-muted)" }}>
          Das Gruppen-Logo kannst du bereits speichern. Für Statistik-Kacheln und das Dashboard benötigst du anschließend eine aktive Saison.
        </div>
      )}

      <div className="dc-border p-6 space-y-4">
        <h3 className="font-display text-2xl flex items-center gap-2"><ImageIcon size={18} /> Gruppen-Logo</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
          <div>
            <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Logo-URL</label>
            <input className="dc-input" value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="https://... oder Datei hochladen" data-testid="dashboard-logo-url" />
          </div>
          <div>
            <label className="dc-btn dc-btn-ghost cursor-pointer inline-flex" data-testid="dashboard-logo-upload-label">
              <Upload size={14} /> Logo hochladen
              <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="dashboard-logo-upload" />
            </label>
          </div>
        </div>
        {logoUrl && (
          <div className="flex items-center gap-3 pt-2">
            <img src={logoUrl} alt="Logo" className="w-16 h-16 object-contain dc-border p-2" data-testid="dashboard-logo-preview" />
            <button className="dc-btn dc-btn-ghost text-xs" onClick={() => setLogoUrl("")}><Trash size={12} /> Entfernen</button>
          </div>
        )}
      </div>

      <div className="dc-border p-6 space-y-4">
        <h3 className="font-display text-2xl flex items-center gap-2"><LayoutGrid size={18} /> Kacheln</h3>
        <div>
          <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Titel (optional, überschreibt Saison-Name im Hero)</label>
          <input className="dc-input" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="dashboard-title-input" />
        </div>
        <div className="space-y-2">
          {widgets.map((w, i) => (
            <div key={w.id} className="dc-border p-3 flex items-center justify-between" data-testid={`widget-${w.id}`}>
              <div className="flex items-center gap-3">
                <span className="font-mono-data text-xs" style={{ color: "var(--dc-muted)" }}>#{i + 1}</span>
                <span className="font-display">{widgetLabels[w.id] || w.id}</span>
              </div>
              <div className="flex items-center gap-1">
                <button className="p-2 hover:text-[color:var(--dc-secondary)]" onClick={() => toggleVis(i)} data-testid={`widget-toggle-${w.id}`}>
                  {w.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button className="p-2 hover:text-[color:var(--dc-secondary)] disabled:opacity-30" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`widget-up-${w.id}`}><ArrowUp size={14} /></button>
                <button className="p-2 hover:text-[color:var(--dc-secondary)] disabled:opacity-30" onClick={() => move(i, 1)} disabled={i === widgets.length - 1} data-testid={`widget-down-${w.id}`}><ArrowDown size={14} /></button>
              </div>
            </div>
          ))}
        </div>
        <button className="dc-btn dc-btn-secondary" onClick={save} data-testid="dashboard-save">Dashboard speichern</button>
      </div>
    </div>
  );
}

function HistoricManager() {
  const [seasons, setSeasons] = useState([]);
  const [players, setPlayers] = useState([]);
  const [seasonId, setSeasonId] = useState("");
  const [entries, setEntries] = useState([]);
  const [current, setCurrent] = useState({});
  const [matchdays, setMatchdays] = useState([]);
  const [matches, setMatches] = useState([]);
  const [scores, setScores] = useState({});
  const reload = async () => {
    const [ss, ps] = await Promise.all([api.listSeasons(), api.listPlayers()]);
    setSeasons(ss); setPlayers(ps);
    const active = ss.find((s) => s.active) || ss[0];
    if (active && !seasonId) setSeasonId(active.id);
  };
  useEffect(() => { reload(); }, []);
  const reloadHistoricMatches = async () => {
    if (!seasonId) return;
    const [historic, days, seasonMatches] = await Promise.all([
      api.listHistoric(seasonId), api.listMatchdays(seasonId), api.listMatches({ season_id: seasonId }),
    ]);
    setEntries(historic); setMatchdays(days); setMatches(seasonMatches);
    setScores(Object.fromEntries(seasonMatches.map((match) => [match.id, match.played ? `${match.legs_p1}:${match.legs_p2}` : ""])));
  };
  useEffect(() => { reloadHistoricMatches(); }, [seasonId]);

  const upd = (pid, field, value) => setCurrent((c) => ({ ...c, [pid]: { ...(c[pid] || {}), [field]: value } }));

  const initFromMembers = () => {
    const s = seasons.find((x) => x.id === seasonId);
    if (!s) return;
    const init = {};
    (s.member_ids || []).forEach((pid) => {
      const existing = entries.find((e) => e.player_id === pid && !e.matchday_number);
      init[pid] = existing || { player_id: pid, played: 0, wins: 0, draws: 0, losses: 0, legs_for: 0, legs_against: 0, s180: 0, average: 0, high_finish: 0, high_finish_count: 0, ausgebullt: 0 };
    });
    setCurrent(init);
  };

  const save = async () => {
    const payload = Object.values(current).filter((e) => e && e.player_id);
    if (!payload.length) { toast.error("Keine Zeilen erfasst"); return; }
    try {
      const res = await api.upsertHistoric(seasonId, payload.map((e) => ({
        matchday_number: e.matchday_number ?? null,
        player_id: e.player_id,
        played: parseInt(e.played) || 0,
        wins: parseInt(e.wins) || 0,
        draws: parseInt(e.draws) || 0,
        losses: parseInt(e.losses) || 0,
        legs_for: parseInt(e.legs_for) || 0,
        legs_against: parseInt(e.legs_against) || 0,
        points: e.points !== "" && e.points !== undefined && e.points !== null ? parseInt(e.points) : null,
        s180: parseInt(e.s180) || 0,
        average: parseFloat(e.average) || 0,
        high_finish: parseInt(e.high_finish) || 0,
        high_finish_count: parseInt(e.high_finish_count) || 0,
        ausgebullt: parseInt(e.ausgebullt) || 0,
      })));
      toast.success(`${res.created} Einträge gespeichert`);
      setCurrent({});
      api.listHistoric(seasonId).then(setEntries);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
  };

  const del = async (eid) => {
    if (!window.confirm("Eintrag löschen?")) return;
    await api.deleteHistoric(eid);
    api.listHistoric(seasonId).then(setEntries);
  };
  const clearAll = async () => {
    if (!window.confirm("ALLE historischen Einträge dieser Saison löschen?")) return;
    await api.clearHistoric(seasonId);
    api.listHistoric(seasonId).then(setEntries);
    toast.success("Gelöscht");
  };

  const saveScores = async () => {
    const toSave = matches.map((match) => {
      const parsed = String(scores[match.id] || "").trim().match(/^(\d{1,2})\s*:\s*(\d{1,2})$/);
      return parsed ? { match, legs_p1: Number(parsed[1]), legs_p2: Number(parsed[2]) } : null;
    }).filter(Boolean);
    if (!toSave.length) { toast.error("Bitte mindestens ein Ergebnis wie 2:1 oder 1:1 eintragen"); return; }
    try {
      await Promise.all(toSave.map(({ match, legs_p1, legs_p2 }) => api.submitHistoricResult(match.id, { legs_p1, legs_p2 })));
      toast.success(`${toSave.length} historische Ergebnisse gespeichert`);
      reloadHistoricMatches();
    } catch (error) { toast.error(error?.response?.data?.detail || "Ergebnisse konnten nicht gespeichert werden"); }
  };

  const updateScore = (matchId, side, value) => setScores((previous) => {
    const [p1 = "", p2 = ""] = String(previous[matchId] || "").split(":");
    return { ...previous, [matchId]: side === "p1" ? `${value}:${p2}` : `${p1}:${value}` };
  });

  const pmap = Object.fromEntries(players.map((p) => [p.id, p]));

  return (
    <div className="space-y-4" data-testid="historic-manager">
      <div className="dc-border p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1">
          <label className="block text-xs uppercase tracking-widest mb-1" style={{ color: "var(--dc-muted)" }}>Saison</label>
          <select className="dc-input" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} data-testid="historic-season-select">
            {seasons.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button className="dc-btn dc-btn-ghost" onClick={initFromMembers} data-testid="historic-init-members">Stammspieler laden</button>
      </div>

      <div className="dc-border p-6 space-y-3">
        <div>
          <h3 className="font-display text-2xl">Ergebnisse schnell eintragen</h3>
          <p className="text-sm" style={{ color: "var(--dc-muted)" }}>
            Spielplan zuerst im Bereich „Spieltage“ erzeugen. Hier kannst du anschließend alle alten Ergebnisse direkt als 2:1 oder auch 1:1 erfassen. Detailwerte werden nicht benötigt.
          </p>
        </div>
        {matchdays.length === 0 && <p className="text-sm" style={{ color: "var(--dc-muted)" }}>Für diese Saison gibt es noch keine Spieltage.</p>}
        {matchdays.map((day) => {
          const dayMatches = matches.filter((match) => match.matchday_id === day.id);
          if (!dayMatches.length) return null;
          return <div key={day.id} className="border-t dc-border pt-3">
            <div className="font-display text-lg mb-2">Spieltag {day.number}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {dayMatches.map((match) => {
                const [scoreP1 = "", scoreP2 = ""] = String(scores[match.id] || "").split(":");
                return <div key={match.id} className="dc-border px-3 py-2 flex items-center gap-2">
                <span className="flex-1 text-sm" style={{ fontFamily: "Manrope" }}>{pmap[match.player1_id]?.name || "?"} - {pmap[match.player2_id]?.name || "?"}</span>
                <div className="flex items-center gap-1 font-mono-data">
                  <input type="number" min="0" max="20" className="dc-input w-12 text-center py-1" value={scoreP1}
                    onChange={(event) => updateScore(match.id, "p1", event.target.value)}
                    aria-label={`Ergebnis ${pmap[match.player1_id]?.name || "Spieler 1"}`}
                    data-testid={`historic-score-${match.id}-p1`} />
                  <span>:</span>
                  <input type="number" min="0" max="20" className="dc-input w-12 text-center py-1" value={scoreP2}
                    onChange={(event) => updateScore(match.id, "p2", event.target.value)}
                    aria-label={`Ergebnis ${pmap[match.player2_id]?.name || "Spieler 2"}`}
                    data-testid={`historic-score-${match.id}-p2`} />
                </div>
              </div>;
              })}
            </div>
          </div>;
        })}
        {matches.length > 0 && <button className="dc-btn dc-btn-secondary" onClick={saveScores} data-testid="historic-scores-save"><Plus size={14} /> Ergebnisse speichern</button>}
      </div>

      <div className="dc-border p-6 space-y-3">
        <div>
          <h3 className="font-display text-2xl">Zusatzwerte / Tabelle direkt eintragen</h3>
          <p className="text-sm" style={{ color: "var(--dc-muted)" }}>
            Für alte Saisons ohne Einzelspiele: gib pro Spieler die Endwerte (S, U, N, Legs, Punkte, Avg, 180er, HF) ein. Wenn du die Ergebnisse oben erfasst hast, lasse Sp, S, U, N, Legs und Punkte hier bei 0 und ergänze nur die verfügbaren Zusatzwerte wie Avg, 180er, High Finish und Ausgebullt - sonst würden die Ergebnisse doppelt zählen.
          </p>
        </div>
        {Object.keys(current).length === 0 && <p className="text-sm" style={{ color: "var(--dc-muted)" }}>Klicke „Stammspieler laden" um zu beginnen.</p>}
        {Object.keys(current).length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono-data">
              <thead style={{ color: "var(--dc-muted)" }}>
                <tr>
                  <th className="text-left px-2">Spieler</th>
                  <th className="text-right px-2">Sp</th><th className="text-right px-2">S</th>
                  <th className="text-right px-2">U</th><th className="text-right px-2">N</th><th className="text-right px-2">Legs+</th>
                  <th className="text-right px-2">Legs-</th><th className="text-right px-2">Pkt</th>
                  <th className="text-right px-2">Avg</th><th className="text-right px-2">180</th>
                  <th className="text-right px-2">HF max.</th><th className="text-right px-2">HF #</th><th className="text-right px-2">Bull</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(current).map(([pid, row]) => (
                  <tr key={pid} className="border-t dc-border" data-testid={`historic-row-${pid}`}>
                    <td className="px-2 py-1" style={{ fontFamily: "Manrope" }}>{pmap[pid]?.name || pid}</td>
                    {["played","wins","draws","losses","legs_for","legs_against","points","average","s180","high_finish","high_finish_count","ausgebullt"].map((f) => (
                      <td key={f} className="px-1"><input className="dc-input font-mono-data text-right py-1 px-2 w-20"
                        type="number" step={f === "average" ? "0.01" : "1"}
                        value={row[f] ?? ""} onChange={(e) => upd(pid, f, e.target.value)}
                        placeholder={f === "points" ? "auto" : "0"}
                        data-testid={`historic-input-${pid}-${f}`} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {Object.keys(current).length > 0 && (
          <button className="dc-btn dc-btn-secondary" onClick={save} data-testid="historic-save-button"><Plus size={14} /> Speichern</button>
        )}
      </div>

      {entries.length > 0 && (
        <div className="dc-border p-6">
          <div className="flex justify-between items-center mb-3">
            <h3 className="font-display text-2xl">Erfasste Einträge ({entries.length})</h3>
            <button className="dc-btn dc-btn-ghost text-xs" onClick={clearAll} data-testid="historic-clear-all"><Trash size={12} /> Alle löschen</button>
          </div>
          <table className="w-full text-sm font-mono-data">
            <thead className="text-xs uppercase" style={{ color: "var(--dc-muted)" }}>
              <tr><th className="text-left px-2 py-1">Spieler</th><th className="text-right px-2">Sp</th><th className="text-right px-2">S/U/N</th><th className="text-right px-2">Legs</th><th className="text-right px-2">Pkt</th><th className="text-right px-2">Avg</th><th className="text-right px-2">180</th><th className="text-right px-2">HF max.</th><th className="text-right px-2">HF #</th><th></th></tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t dc-border" data-testid={`historic-entry-${e.id}`}>
                  <td className="px-2 py-1" style={{ fontFamily: "Manrope" }}>{pmap[e.player_id]?.name || e.player_id.slice(0, 8)}</td>
                  <td className="text-right px-2">{e.played}</td>
                  <td className="text-right px-2">{e.wins}/{e.draws || 0}/{e.losses}</td>
                  <td className="text-right px-2">{e.legs_for}:{e.legs_against}</td>
                  <td className="text-right px-2">{e.points ?? e.wins * 3 + (e.draws || 0)}</td>
                  <td className="text-right px-2">{e.average}</td>
                  <td className="text-right px-2">{e.s180}</td>
                  <td className="text-right px-2">{e.high_finish}</td>
                  <td className="text-right px-2">{e.high_finish_count || 0}</td>
                  <td className="text-right"><button className="p-1 hover:text-[color:var(--dc-primary)]" onClick={() => del(e.id)} data-testid={`historic-delete-${e.id}`}><Trash2 size={12} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BackupManager() {
  const [importing, setImporting] = useState(false);
  const doExport = async () => {
    const data = await api.exportBackup();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `aissen180-sicherung-${new Date().toISOString().split("T")[0]}.json`; a.click();
    URL.revokeObjectURL(url); toast.success("Backup heruntergeladen");
  };
  const doImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!window.confirm("Achtung: Alle Daten werden ersetzt. Fortfahren?")) return;
    setImporting(true);
    try {
      const text = await file.text(); const json = JSON.parse(text);
      const res = await api.importBackup(json);
      toast.success(`Import: ${res.imported.players} Spieler, ${res.imported.seasons} Saisons, ${res.imported.matchdays || 0} Spieltage, ${res.imported.matches} Partien`);
    } catch (err) { toast.error("Import fehlgeschlagen: " + (err?.message || "")); }
    finally { setImporting(false); e.target.value = ""; }
  };
  const reset = async () => {
    if (!window.confirm("ALLE Daten unwiderruflich löschen?")) return;
    try { await api.adminReset(); toast.success("Datenbank geleert"); }
    catch { toast.error("Fehler"); }
  };
  return (
    <div className="space-y-4" data-testid="backup-manager">
      <div className="dc-border p-6">
        <h3 className="font-display text-2xl mb-2">Lokale Sicherung</h3>
        <p className="text-sm mb-4" style={{ color: "var(--dc-muted)" }}>Alle Daten (Spieler, Saisons, Spieltage, Partien) als JSON-Datei speichern. Import ersetzt alle aktuellen Daten.</p>
        <div className="flex flex-wrap gap-3">
          <button className="dc-btn" onClick={doExport} data-testid="backup-export-button"><Download size={14} /> Backup exportieren</button>
          <label className="dc-btn dc-btn-ghost cursor-pointer" data-testid="backup-import-label">
            <Upload size={14} /> {importing ? "Importiere..." : "Backup importieren"}
            <input type="file" accept="application/json" className="hidden" onChange={doImport} data-testid="backup-import-input" />
          </label>
        </div>
      </div>
      <div className="dc-border p-6" style={{ borderColor: "var(--dc-primary)" }}>
        <h3 className="font-display text-2xl mb-2" style={{ color: "var(--dc-primary)" }}>Gefahrenzone</h3>
        <p className="text-sm mb-4" style={{ color: "var(--dc-muted)" }}>Setzt die komplette Datenbank zurück. Nur nach Backup verwenden!</p>
        <button className="dc-btn" style={{ background: "var(--dc-primary)" }} onClick={reset} data-testid="admin-reset-button">
          <Trash size={14} /> Alles zurücksetzen
        </button>
      </div>
    </div>
  );
}
